import { FC, useContext, useMemo } from "react";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles/exports";
import GapsByAgeChart from "@app/overviews/enterprise-compliance-dashboard/components/GapsByAgeChart";
import { EcdGenerateReportContext } from "@app/overviews/enterprise-compliance-dashboard/contexts/EcdGenerateReportContext";
import { filterToTimeFrame } from "@app/overviews/enterprise-compliance-dashboard/utility";

interface GapsByAgeDrillInData {
  [wafflesKeyColumnName]: string;
  dateRange: string;
  gapAge30: string;
  percentOfTotalGaps30: string;
  gapAge60: string;
  percentOfTotalGaps60: string;
  gapAge90: string;
  percentOfTotalGaps90: string;
  gapAgeOver90: string;
  percentOfTotalGapsOver90: string;
}

const columnConfig: ColumnConfig = {
  dateRange: {
    displayText: "Date Range",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "date-range",
  },
  gapAge30: {
    displayText: "< 30 Days",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "30-days-count",
  },
  gapAge60: {
    displayText: "31-60 Days",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "60-days-count",
  },
  gapAge90: {
    displayText: "61-90 Days",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "90-days-count",
  },
  gapAgeOver90: {
    displayText: "> 90 Days",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "over-90-count",
  },
  percentOfTotalGaps30: {
    displayText: "< 30 Days %",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "30-days-percent",
  },
  percentOfTotalGaps60: {
    displayText: "31-60 Days %",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "60-days-percent",
  },
  percentOfTotalGaps90: {
    displayText: "61-90 Days %",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "90-days-percent",
  },
  percentOfTotalOver90: {
    displayText: "> 90 Days %",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "over-90-percent",
  },
}


const GapsByAgeExport: FC = () => {
  const wizardContext = useContext(EcdGenerateReportContext);
  const title = "Gaps By Age";

  const slicedGapsByAgeData = useMemo(() => {
    return filterToTimeFrame(wizardContext.gapsByAgeData, wizardContext.gapsByAgeTimeFrame);
  }, [wizardContext.gapsByAgeData, wizardContext.gapsByAgeTimeFrame]);

  const tableData = slicedGapsByAgeData.map((row) => {
    return {
      /* oxlint-disable sort-keys */
      [wafflesKeyColumnName]: row.month,
      dateRange: row.month,
      gapAge30: row.gapAge30.toLocaleString(),
      percentOfTotalGaps30: row.percentOfTotalGaps30.toLocaleString(),
      gapAge60: row.gapAge60.toLocaleString(),
      percentOfTotalGaps60: row.percentOfTotalGaps60.toLocaleString(),
      gapAge90: row.gapAge90.toLocaleString(),
      percentOfTotalGaps90: row.percentOfTotalGaps90.toLocaleString(),
      gapAgeOver90: row.gapAgeOver90.toLocaleString(),
      percentOfTotalGapsOver90: row.percentOfTotalGapsOver90.toLocaleString()
      /* oxlint-enable sort-keys */
    }
  });

  //cut off array when rows will exceed one page in export
  if (tableData.length > 15)
    tableData.length = 15;

  return (
    <div className="ecd-export__wrapper">
      <div style={{ display: "flex" }}>
        <h2 className="qes-dashboard-tile__title ecd-export__head">{title}</h2>
        <h2 className="qes-dashboard-tile__title ecd-export__head" style={{ marginLeft: "auto", order: "2" }}>{"Time Frame: " + wizardContext.gapsByAgeTimeFrame}</h2>
      </div>
      <br />
      <GapsByAgeChart
        gapsByAgeData={slicedGapsByAgeData}
        adequacyBreakdownData={wizardContext.adequacyBreakdownData}
        gapsArray={wizardContext.gapsArray}
        isExport={true}
      />
      <br />
      {wizardContext.addGraphDetails && (
        <div className="ecd-export__table">
          <h2 className="qes-dashboard-tile__title ecd-export__head">{title + " Details"}</h2>
          <Waffles<GapsByAgeDrillInData>
            columns={columnConfig}
            data={tableData}
            fullWidth={true}
            loading={tableData.length === 0}
            testId="ecd-export-gaps-by-age__table"
          />
        </div>
      )}
    </div>
  );
}

export default GapsByAgeExport;